import { useState, useEffect } from 'react';

// Types
interface CitationTooltipState {
  visible: boolean;
  title: string;
  url: string;
  content: string;
  x: number;
  y: number;
  sourceType?: 'website' | 'document' | 'youtube' | 'phone' | 'email' | 'social';
}

interface ChatMessage {
  message_id?: string;
  id?: string;
  citation_sources?: Array<{
    title?: string;
    url?: string;
    text?: string;
    cited_text?: string;
    sentence?: string;
    source_type?: string;
  }>;
  metadata?: {
    citations?: Array<{
      title?: string;
      url?: string;
      text?: string;
      cited_text?: string;
      sentence?: string;
      source_type?: string;
    }>;
    citation_sources?: Array<{
      title?: string;
      url?: string;
      text?: string;
      cited_text?: string;
      sentence?: string;
      source_type?: string;
    }>;
  };
}

/**
 * Custom hook for citation tooltip functionality
 * Extracts exact logic from ChatHistory.tsx for consistent behavior
 */
export const useCitationTooltip = (messages: ChatMessage[], triggerReInit?: any) => {
  const [citationTooltip, setCitationTooltip] = useState<CitationTooltipState & { position?: 'top' | 'bottom'; horizontalPosition?: 'left' | 'center' | 'right' }>({
    visible: false,
    title: '',
    url: '',
    content: '',
    x: 0,
    y: 0,
    position: 'bottom',
    horizontalPosition: 'center',
    sourceType: undefined
  });

  // Citation tooltip handling with exact logic from ChatHistory
  useEffect(() => {
    let tooltipTimeout: NodeJS.Timeout | null = null;
    let isHoveringTooltip = false;
    
    const handleCitationEnter = (event: Event) => {
      const target = event.target as HTMLElement;
      if (!target.classList.contains('citation-marker')) return;
      
      // Clear any pending leave timeout
      if (tooltipTimeout) {
        clearTimeout(tooltipTimeout);
        tooltipTimeout = null;
      }
      
      const citationNumber = target.getAttribute('data-source-number');
      const messageElement = target.closest('.response-content-direct');
      const messageId = messageElement?.getAttribute('data-message-id');
      
      if (citationNumber && messageId) {
        const message = messages.find(m => {
          const msgId = m.message_id || m.id;
          return msgId && msgId.toString() === messageId;
        });
        
        // Check both metadata.citations and citation_sources for compatibility
        const citations = message?.metadata?.citations || message?.citation_sources || message?.metadata?.citation_sources;
        
        if (citations) {
          const citation = citations[parseInt(citationNumber) - 1];
          
          if (citation) {
            const rect = target.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const tooltipWidth = 320; // Max width of tooltip (reduced for better UX)
            
            // Calculate marker position and viewport constraints
            const markerCenterX = rect.left + rect.width / 2;
            const markerLeft = rect.left;
            const markerRight = rect.right;
            
            // Check if marker is near edges
            const edgeThreshold = 100; // Distance from edge to trigger edge alignment
            const isNearLeftEdge = markerLeft < edgeThreshold;
            const isNearRightEdge = markerRight > viewportWidth - edgeThreshold;
            
            // Determine horizontal position
            let horizontalPosition: 'left' | 'center' | 'right' = 'center';
            let tooltipX = markerCenterX;
            
            if (isNearLeftEdge) {
              horizontalPosition = 'left';
              tooltipX = markerLeft; // Align with left edge of marker
            } else if (isNearRightEdge) {
              horizontalPosition = 'right';
              tooltipX = markerRight; // Align with right edge of marker
            } else {
              // Center alignment - ensure tooltip doesn't go off screen
              const halfTooltipWidth = tooltipWidth / 2;
              if (markerCenterX - halfTooltipWidth < 10) {
                // Too close to left edge, shift right
                tooltipX = halfTooltipWidth + 10;
              } else if (markerCenterX + halfTooltipWidth > viewportWidth - 10) {
                // Too close to right edge, shift left
                tooltipX = viewportWidth - halfTooltipWidth - 10;
              }
            }
            
            // Smart vertical positioning: check if marker is near bottom of viewport
            const markerBottom = rect.bottom;
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - markerBottom;
            const tooltipHeight = 250; // Estimated tooltip height
            
            // Show tooltip above if not enough space below, otherwise show below
            const position: 'top' | 'bottom' = spaceBelow < tooltipHeight ? 'top' : 'bottom';
            const tooltipY = position === 'top' ? rect.top - 5 : rect.bottom + 5;
            
            const tooltipData = {
              visible: true,
              title: citation.title || 'Source',
              url: citation.url || '',
              content: citation.text || citation.cited_text || citation.sentence || '',
              x: tooltipX,
              y: tooltipY,
              position: position as 'top' | 'bottom',
              horizontalPosition: horizontalPosition,
              sourceType: citation.source_type as 'website' | 'document' | 'youtube' | 'phone' | 'email' | 'social' | undefined
            };
            setCitationTooltip(tooltipData);
          }
        }
      }
    };
    
    const handleCitationLeave = () => {
      // Only hide if not hovering over tooltip
      tooltipTimeout = setTimeout(() => {
        if (!isHoveringTooltip) {
          setCitationTooltip({ visible: false, title: '', url: '', content: '', x: 0, y: 0, position: 'bottom', horizontalPosition: 'center', sourceType: undefined });
        }
      }, 50);
    };

    // Global tooltip hover handlers
    const handleTooltipEnter = () => {
      isHoveringTooltip = true;
      if (tooltipTimeout) {
        clearTimeout(tooltipTimeout);
        tooltipTimeout = null;
      }
    };

    const handleTooltipLeave = () => {
      isHoveringTooltip = false;
      tooltipTimeout = setTimeout(() => {
        setCitationTooltip({ visible: false, title: '', url: '', content: '', x: 0, y: 0, position: 'bottom', horizontalPosition: 'center' });
      }, 50);
    };

    // Store handlers for cleanup
    (window as any).tooltipHandlers = {
      handleTooltipEnter,
      handleTooltipLeave
    };
    
    // Re-attach event listeners with a slight delay to ensure DOM is ready
    const attachListeners = () => {
      const citationMarkers = document.querySelectorAll('.citation-marker');
      citationMarkers.forEach(marker => {
        // Remove inline events
        marker.removeAttribute('onmouseover');
        marker.removeAttribute('onmouseout');
        
        // Remove existing listeners to avoid duplicates
        marker.removeEventListener('mouseover', handleCitationEnter);
        marker.removeEventListener('mouseout', handleCitationLeave);
        
        // Add our event listeners
        marker.addEventListener('mouseover', handleCitationEnter);
        marker.addEventListener('mouseout', handleCitationLeave);
      });
    };
    
    // Initial attachment
    attachListeners();
    
    // Re-attach after DOM changes (for tab switching)
    const timeoutId = setTimeout(attachListeners, 100);
    
    // Add MutationObserver to detect tab switches and re-attach listeners
    const observer = new MutationObserver((mutations) => {
      // Check if any mutation affected the chat message area
      const needsReattach = mutations.some(mutation => {
        const target = mutation.target as HTMLElement;
        return target.classList?.contains('response-tab') || 
               target.classList?.contains('sources-tab') ||
               target.classList?.contains('tab-content') ||
               target.classList?.contains('active') ||
               mutation.addedNodes.length > 0;
      });
      
      if (needsReattach) {
        // Debounce the re-attachment
        setTimeout(attachListeners, 50);
      }
    });
    
    // Observe the chat container for changes
    const chatContainer = document.querySelector('.chat-history, .chat-interface, .response-container');
    if (chatContainer) {
      observer.observe(chatContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }
    
    return () => {
      clearTimeout(timeoutId);
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      observer.disconnect();
      const citationMarkers = document.querySelectorAll('.citation-marker');
      citationMarkers.forEach(marker => {
        marker.removeEventListener('mouseover', handleCitationEnter);
        marker.removeEventListener('mouseout', handleCitationLeave);
      });
    };
  }, [messages, triggerReInit]);

  // Tooltip event handlers to be passed to CitationTooltip component
  const tooltipEventHandlers = {
    onMouseEnter: () => {
      const handlers = (window as any).tooltipHandlers;
      if (handlers) handlers.handleTooltipEnter();
    },
    onMouseLeave: () => {
      const handlers = (window as any).tooltipHandlers;
      if (handlers) handlers.handleTooltipLeave();
    }
  };

  return {
    citationTooltip,
    tooltipEventHandlers
  };
};